/**
 * Shared heading block for landing sections.
 *
 * What: Renders an optional eyebrow, an h2 title, an optional body paragraph, and an optional trailing slot.
 * Why: Three sections (challenges, how-we-help, program-facts) duplicated this exact block; extracting keeps typography and spacing consistent.
 * When: Update when the canonical landing-section title rhythm changes.
 */
import type { ReactNode } from 'react';
import { Stack, Typography } from '@mui/material';

import { colors } from '@/components/landing/landingTokens';

interface LandingSectionHeadingProps {
  titleId: string;
  title: string;
  eyebrow?: string;
  body?: string;
  trailing?: ReactNode;
}

function LandingSectionHeading({
  titleId,
  title,
  eyebrow,
  body,
  trailing,
}: LandingSectionHeadingProps) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        maxWidth: { xs: '100%', sm: 760 },
        width: '100%',
        minWidth: 0,
      }}
    >
      {eyebrow && (
        <Typography
          variant="overline"
          sx={{
            letterSpacing: 2,
            color: colors.accentCobalt,
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {eyebrow}
        </Typography>
      )}
      <Typography
        id={titleId}
        component="h2"
        sx={{
          fontSize: { xs: 28, sm: 32, md: 36 },
          fontWeight: 800,
          letterSpacing: -0.5,
          color: colors.ink,
          lineHeight: 1.15,
          overflowWrap: 'break-word',
        }}
      >
        {title}
      </Typography>
      {body && (
        <Typography
          variant="body1"
          sx={{
            color: colors.inkMuted,
            lineHeight: 1.65,
            fontSize: { xs: 15, sm: 16 },
            overflowWrap: 'break-word',
          }}
        >
          {body}
        </Typography>
      )}
      {trailing}
    </Stack>
  );
}

export default LandingSectionHeading;
